<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useUserProfileQuery } from '@modules/account'
import Box from '@uiKit/elements/Box.vue'
import AskSliceyConversation from '../components/AskSliceyConversation.vue'
import AskSliceyComposer from '../components/AskSliceyComposer.vue'
import { useSendSliceyMessageMutation } from '../api/useSendSliceyMessageMutation'
import { useAskSliceyStore } from '../store/askSliceyStore'
import { getErrorMessage } from '@core/api/normalizeError'

const route = useRoute()
const store = useAskSliceyStore()
const profileQuery = useUserProfileQuery()
const sendMessage = useSendSliceyMessageMutation()

const STARTER_PROMPTS = [
  'How is my portfolio doing?',
  'Show me my pending orders',
  "What's a good property for AED 500?",
  'Explain the fees on my last order',
]

// Floor on visible "thinking" time. Fast responses can return in <50ms; that
// reads as if the AI didn't actually consider the question. Holding for ~900ms
// matches what users expect from a real reasoning system.
const THINKING_FLOOR_MS = 900

const ID_RANDOM_LENGTH = 5
const ID_RANDOM_OFFSET = 2

const userId = computed(() => profileQuery.data.value?.userId ?? '')

const messages = computed(() => store.messages)

// Local thinking flag combined with the mutation's own pending state so the
// "Slicey is thinking" indicator stays up for the full floor delay even if
// the backend returns instantly (or errors before the floor expires).
const isHoldingThinking = ref(false)
const isThinking = computed(() => sendMessage.isPending.value || isHoldingThinking.value)

const contextLabel = computed(() => `Analysing: ${route.path} · your profile`)

const nextMessageId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(ID_RANDOM_OFFSET, ID_RANDOM_OFFSET + ID_RANDOM_LENGTH)}`

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const handleSubmit = async (text: string) => {
  if (isThinking.value) return

  store.appendMessage({
    id: nextMessageId('u'),
    author: 'user',
    text,
    createdAt: new Date().toISOString(),
  })

  isHoldingThinking.value = true
  const floor = wait(THINKING_FLOOR_MS)

  try {
    const [response] = await Promise.all([
      sendMessage.mutateAsync({
        prompt: text,
        currentScreen: route.path,
        userId: userId.value,
      }),
      floor,
    ])
    store.appendMessage({
      id: nextMessageId('a'),
      author: 'assistant',
      text: response.reply,
      createdAt: new Date().toISOString(),
      suggestions: response.suggestions,
    })
  } catch (error) {
    await floor
    const message = getErrorMessage(error, '')
    store.appendMessage({
      id: nextMessageId('e'),
      author: 'assistant',
      text: message || "I couldn't reach Slicey right now. Please try again in a moment.",
      createdAt: new Date().toISOString(),
    })
  } finally {
    isHoldingThinking.value = false
  }
}
</script>

<template>
  <Box direction="col" class="slicey-body">
    <AskSliceyConversation
      :messages="messages"
      :is-thinking="isThinking"
      :starter-prompts="STARTER_PROMPTS"
      @pick="handleSubmit"
    />
    <AskSliceyComposer
      :disabled="isThinking"
      :context-label="contextLabel"
      @submit="handleSubmit"
    />
  </Box>
</template>

<style scoped>
.slicey-body {
  height: 100%;
  min-height: 0;
}
</style>
